import React, { Component } from "react";
import _get from "lodash.get";

import Text from "components/common/Text";
import Loader from "components/common/Loader";
import Modal from "components/common/Modal";
import Stack from "components/common/Stack";
import Image from "components/common/Image";
import TextField from "components/common/TextField";

import JarvisButton from "components/JarvisButton";
import StockCard from "components/StockCard";
import JarvisNote from "components/JarvisNote";

import { config } from "config";
import { thirdpartyApis } from "stores/thirdparty/thirdpartyApis";
import { thirdpartyParsers } from "stores/thirdparty/thirdpartyParsers";
import { validationHelper } from "helper/validation";

import { withRouter } from "react-router-dom";

import { tracker } from "library/tracker";
import { localStore } from "stores/localStorage";
import OtpInput from "react-otp-input";
import Button from "components/common/Button";

const OTP_RESEND_SECONDS = 30;
const SLOW_ORDER_SECONDS = 20; // after this, the "please wait" note switches to the "still processing" text
// Finish stays disabled while a stock is pending, until the user has checked status this many times
const MIN_STATUS_CHECKS_BEFORE_FINISH = 2;
const FINAL_ORDER_STATUSES = ["complete", "completed", "rejected", "cancelled", "canceled"];

class BrokerNaviaContainer extends Component {

    constructor(props) {
        super(props);
        this.otpTimerInterval = null;
        this.state = {
            isLoading: false,
            isAuthLoading: false,
            authFormModalOpen: false,
            otpSentLoading: false,
            otpSuccessResponse: "",
            otpFailureResponse: "",
            otpResendSeconds: 0,
            showOtpField: false,
            authFormData: {
                username: "",
                password: "",
                otp: ""
            },
            authFormError: {
                username: "",
                password: "",
                otp: ""
            },
            processDataModalOpen: false,
            isOrderLoading: false,
            isOrderPlaced: false,
            isOrderSlow: false,
            statusCheckCount: 0,
            processData: {
                brokerName: "",
                brokerPlatform: "",
                processType: "",
                productCode: "",
                brokerId: "",
                oneStockCallId: "",
            },
        };
    }

    componentDidMount() {
        this.setProcessData();
    }

    componentWillUnmount() {
        this.clearOtpTimer();
        clearTimeout(this.slowOrderTimer);
        window.removeEventListener("beforeunload", this.handleBeforeUnload);
    }

    // Navia can take a while to place an order; warn before refresh/close while it is in progress
    handleBeforeUnload = (e) => {
        e.preventDefault();
        e.returnValue = "";
    };

    clearOtpTimer = () => {
        if (this.otpTimerInterval) {
            clearInterval(this.otpTimerInterval);
            this.otpTimerInterval = null;
        }
    };

    startOtpTimer = () => {
        this.clearOtpTimer();
        this.setState({ otpResendSeconds: OTP_RESEND_SECONDS });
        this.otpTimerInterval = setInterval(() => {
            this.setState((prev) => {
                if (prev.otpResendSeconds <= 1) {
                    this.clearOtpTimer();
                    return { otpResendSeconds: 0 };
                }
                return { otpResendSeconds: prev.otpResendSeconds - 1 };
            });
        }, 1000);
    };

    resolveLoginMessage = (response) => {
        if (typeof response === "string") {
            return response;
        }
        if (response && typeof response.message === "string") {
            return response.message;
        }
        if (response && response.result && typeof response.result.message === "string") {
            return response.result.message;
        }
        if (response && response.response && typeof response.response === "string") {
            return response.response;
        }
        return "";
    };

    getApiErrorMessage = (error) => {
        const raw =
            _get(error, "response.data.message") ||
            _get(error, "response.data.result.message") ||
            _get(error, "data.message") ||
            _get(error, "message") ||
            "";

        const lower = String(raw).toLowerCase();
        if (!raw || lower.includes("status code 400") || lower.includes("request failed")) {
            const apiMsg = _get(error, "response.data.message");
            if (apiMsg && !String(apiMsg).toLowerCase().includes("status code")) {
                return this.toUserFriendlyMessage(apiMsg);
            }
            return "Something went wrong. Please try again.";
        }
        return this.toUserFriendlyMessage(raw);
    };

    toUserFriendlyMessage = (message) => {
        const lower = String(message || "").toLowerCase();
        if (lower.includes("error invoke activity")) {
            return "Unable to reach Navia right now. Please wait a few seconds and try again.";
        }
        if (lower.includes("error occured while process") || lower.includes("error occurred while process")) {
            return "Login could not be completed. Please request a new OTP and try again.";
        }
        if (lower.includes("incorrect") || lower.includes("invalid otp")) {
            return "The OTP you entered is incorrect. Please try again.";
        }
        if (lower.includes("suspend")) {
            return "Your Navia account appears to be temporarily locked. Please try again later or contact Navia support.";
        }
        if (lower.includes("failed to connect") || lower.includes("curl failed")) {
            return "Unable to connect to Navia. Please check your connection and try again.";
        }
        if (lower.includes("request failed with status code")) {
            return "Something went wrong. Please try again.";
        }
        return message;
    };

    setProcessData = () => {
        // Embedded use (opened on the current screen) passes processData; the /customer/broker page uses route state
        const source = this.props.processData || _get(this.props.location, "state", {}) || {};
        const brokerName = _get(source, "brokerName", "");
        const brokerPlatform = _get(source, "brokerPlatform", "");
        const processType = _get(source, "processType", "");
        const productCode = _get(source, "productCode", "");
        const brokerId = _get(source, "brokerId", "");
        const oneStockCallId = _get(source, "onestockCallId", "")
        const stockQuantity = _get(source, "stockQuantity", "")

        if (brokerName || brokerPlatform || (productCode !== config.productCodes.PORTFOLIO && processType)) {
            this.setState(
                { processData: { brokerName, brokerPlatform, processType, productCode, brokerId, oneStockCallId, stockQuantity } },
                () => {
                    this.initProcess();
                }
            );
        } else {
            this.props.onError({
                reason: "No Broker selected",
                origin: "componentDidMount > not found = brokerName | brokerPlatform | processType",
            });
        }
    }

    initProcess = async () => {
        try {
            const { processData } = this.state;
            const { loginProfile } = this.props
            this.setState({ isLoading: true });
            const isSsoCustomer = localStore.isSsoCustomer()
            const ssoCustomerDetails = localStore.getSsoCustomerDetails();

            const prevTokenResponse =
                await thirdpartyApis.brokerPreviousAccessToken({
                    brokerPlatform: processData.brokerPlatform,
                    productCode: processData.productCode,
                    brokerId: processData.brokerId,
                });
            const { valid, clientId } =
                thirdpartyParsers.brokerPreviousAccessToken(prevTokenResponse, processData.productCode);

            if (!valid && !isSsoCustomer) {
                this.setState({ authFormModalOpen: true });
                return;
            }
            let clientID = clientId
            if (!clientId && isSsoCustomer)
                clientID = loginProfile.ssoClientId ? loginProfile.ssoClientId : ''

            await this.initOrderProcess({ clientId: clientID });
        } catch (error) {
            this.props.onError({
                reason: "Something went wrong! Please try again later!",
                origin: "initProcess > catch block",
                error,
            });
        } finally {
            this.setState({ isLoading: false });
        }
    };

    initOrderProcess = async ({ clientId } = {}) => {
        const { processData } = this.state;
        if (processData.productCode === config.productCodes.RMS) {
            await this.props.fetchHoldingData({
                brokerName: processData.brokerName,
                brokerPlatform: processData.brokerPlatform,
                productCode: processData.productCode,
                brokerId: processData.brokerId,
                clientId,
            });
            return;
        }

        tracker.broker.brokerStockList({ broker_name: processData.brokerName });

        const preOrderData = await this.props.getPreOrderData({
            brokerName: processData.brokerName,
            brokerPlatform: processData.brokerPlatform,
            processType: processData.processType,
            productCode: processData.productCode,
            brokerId: processData.brokerId,
            onestockCallId: processData.oneStockCallId,
            stockQuantity: processData.stockQuantity,
        });
        tracker.broker.brokerStockListSuccess({ broker_name: processData.brokerName });
        if (!preOrderData) {
            tracker.broker.brokerStockListFailed({ broker_name: processData.brokerName, error_msg: "" });
            return;
        }

        this.setState({ processDataModalOpen: true });
    };

    handleOrderdataModalClose = () => {
        this.setState({
            processDataModalOpen: false
        });
        this.props.onCancel();
    }

    handleAuthFormChange = ({ name, value }) => {
        const { authFormData } = this.state;
        authFormData[name] = value;
        this.setState({ authFormData });
    };

    handleAuthFormValidation = ({ requireOtp = false } = {}) => {
        const { authFormData, authFormError } = this.state;

        const usernameValidation = validationHelper.required(
            authFormData.username
        );
        authFormError.username = usernameValidation.message;

        const passwordValidation = validationHelper.required(
            authFormData.password
        );
        authFormError.password = passwordValidation.message;

        let otpValid = true;
        if (requireOtp) {
            const otpValidation = validationHelper.required(authFormData.otp);
            authFormError.otp = otpValidation.message;
            otpValid = otpValidation.isValid && String(authFormData.otp).length === 6;
        } else {
            authFormError.otp = "";
        }

        this.setState({ authFormError });
        return (
            usernameValidation.isValid &&
            passwordValidation.isValid &&
            otpValid
        );
    };

    handleAuthFormSubmit = async (value) => {
        const requireOtp = value === "LOGIN";
        if (!this.handleAuthFormValidation({ requireOtp })) {
            return;
        }
        const { authFormData, processData } = this.state;
        tracker.broker.brokerLogin({ broker_name: processData.brokerName });
        if (value === "SEND_OTP") {
            this.setState({ otpSentLoading: true, otpFailureResponse: "", otpSuccessResponse: "" });
        }

        if (value === "LOGIN") {
            this.setState({ isAuthLoading: true, otpFailureResponse: "", otpSuccessResponse: "" });
        }
        try {
            const response = await thirdpartyApis.brokerLogin({
                brokerPlatform: processData.brokerPlatform,
                brokerName: processData.brokerName,
                username: authFormData.username,
                password: authFormData.password,
                productCode: processData.productCode,
                brokerId: processData.brokerId,
                otp: value === "LOGIN" && authFormData.otp ? authFormData.otp : ""
            });
            const responseMessage = this.resolveLoginMessage(response);

            if (responseMessage === "Login successful.") {
                this.clearOtpTimer();
                this.setState({ authFormModalOpen: false, otpFailureResponse: "" });
                tracker.broker.brokerLoginSuccess({ broker_name: processData.brokerName });
                await this.initOrderProcess({ clientId: authFormData.username });
                return;
            }
            if (responseMessage === "OTP Sent Successfully.") {
                this.setState({
                    otpSuccessResponse: responseMessage,
                    otpFailureResponse: "",
                    showOtpField: true,
                    authFormData: { ...authFormData, otp: "" },
                });
                this.startOtpTimer();
                tracker.broker.brokerLoginSuccess({ broker_name: processData.brokerName });
                return;
            }

            // Unexpected payload — keep user on login form
            this.setState({
                otpFailureResponse: responseMessage || "Login failed. Please try again.",
            });
            tracker.broker.brokerLoginFailed({
                broker_name: processData.brokerName,
                error_msg: responseMessage || "unexpected_login_response",
            });
        } catch (error) {
            const errorMessage = this.getApiErrorMessage(error);
            tracker.broker.brokerLoginFailed({ broker_name: processData.brokerName, error_msg: errorMessage });
            // Stay on login popup — do not call onError (that shows Transaction Failed and restarts journey)
            this.setState({
                otpFailureResponse: errorMessage,
                otpSuccessResponse: "",
                authFormModalOpen: true,
            });
        } finally {
            this.setState({ isAuthLoading: false, otpSentLoading: false });
        }
    };

    handleAuthFormModalClose = ({ process } = {}) => {
        this.clearOtpTimer();
        this.setState({
            authFormModalOpen: false,
            showOtpField: false,
            otpResendSeconds: 0,
            otpSuccessResponse: "",
            otpFailureResponse: "",
            authFormData: {
                username: "",
                password: "",
                otp: "",
            },
            authFormError: {
                username: "",
                password: "",
                otp: "",
            },
        });
        if (!process) {
            if (this.props.onClose) {
                this.props.onClose();
            } else {
                this.props.history.goBack();
            }
        }
    };

    handleOrderProcessClick = async (action) => {
        const { processData } = this.state;
        const { updateStocksInPreOrder, preOrderData, setPostOrderData } = this.props;
        if (action === "PLACE_ORDER") {
            tracker.broker.brokerPlaceNewOrder({ broker_name: processData.brokerName });
            this.setState({ isOrderLoading: true });
            window.addEventListener("beforeunload", this.handleBeforeUnload);
            this.slowOrderTimer = setTimeout(() => {
                this.setState({ isOrderSlow: true });
            }, SLOW_ORDER_SECONDS * 1000);
            try {
                const placeOrderResponse =
                    await thirdpartyApis.brokerPlaceOrder({
                        brokerPlatform: processData.brokerPlatform,
                        brokerName: processData.brokerName,
                        type: processData.processType,
                        brokerId: processData.brokerId,
                        oneStockCallId: processData.oneStockCallId || null,
                        productCode: processData.productCode,
                    });

                const placeOrderParsed =
                    thirdpartyParsers.brokerPlaceOrder(placeOrderResponse);

                setPostOrderData({
                    totalStocks: placeOrderParsed.totalStocks,
                    completedStocks: placeOrderParsed.completedStocks
                });
                updateStocksInPreOrder(placeOrderParsed.list);
                this.setState({ isOrderPlaced: true });
                tracker.broker.brokerPlaceNewOrdersuccess({ broker_name: processData.brokerName });
            } catch (error) {
                tracker.broker.brokerPlaceNewOrderFailed({ broker_name: processData.brokerName, error_msg: error.message });
                this.props.onError({
                    reason: "Something went wrong! Please try again later!",
                    origin: "handleOrderProcessClick > PLACE_ORDER",
                    error,
                });
            } finally {
                window.removeEventListener("beforeunload", this.handleBeforeUnload);
                clearTimeout(this.slowOrderTimer);
                this.setState({ isOrderLoading: false, isOrderSlow: false });
            }
            return;
        }

        if (action === "CHECK_ORDER_STATUS") {
            tracker.broker.checkOrderStatus({ broker_name: processData.brokerName });
            this.setState({ isOrderLoading: true });
            try {
                const stocksSymbol = preOrderData.stockDetails.map(s => s.symbol)

                const checkOrderStatusResponse =
                    await thirdpartyApis.brokerCheckOrderStatus({
                        brokerPlatform: processData.brokerPlatform,
                        brokerName: processData.brokerName,
                        brokerId: processData.brokerId,
                        type: processData.processType,
                        stocks: stocksSymbol,
                        oneStockCallId: processData.oneStockCallId || null,
                        productCode: processData.productCode
                    });
                tracker.broker.checkOrderStatusSuccess({ broker_name: processData.brokerName });
                const checkOrderStatusParsed = thirdpartyParsers.brokerPlaceOrder(
                    checkOrderStatusResponse
                );

                setPostOrderData({
                    totalStocks: checkOrderStatusParsed.totalStocks,
                    completedStocks: checkOrderStatusParsed.completedStocks
                });
                updateStocksInPreOrder(checkOrderStatusParsed.list);
            } catch (error) {
                tracker.broker.checkOrderStatusFailed({ broker_name: processData.brokerName });
            } finally {
                // counted even if the check fails, so the user can still finish when status is unavailable
                this.setState((prev) => ({
                    isOrderLoading: false,
                    statusCheckCount: prev.statusCheckCount + 1,
                }));
            }
            return;
        }

        if (action === "FINISH_ORDER") {
            tracker.broker.finish({ broker_name: processData.brokerName });
            const isRejected = preOrderData.stockDetails.every(
                (sd) => sd.status == "rejected"
            );

            this.setState({ processDataModalOpen: false }, () => {
                if (isRejected) {
                    this.props.onError({
                        reason: "Order has been rejected by platform.",
                    });
                    return;
                }
                this.props.onSuccess();
                tracker.broker.finishSuccess({ broker_name: processData.brokerName });
            });
        }
    };

    renderLoginForm() {
        const {
            isAuthLoading,
            authFormData,
            authFormError,
            processData: { brokerName },
            otpSentLoading,
            otpSuccessResponse,
            otpFailureResponse,
            otpResendSeconds,
            showOtpField,
        } = this.state;
        const imageSrc = _get(config.broker[brokerName], `logoUrl`, "") || _get(config.broker.navia, `logoUrl`, "");
        const otpContainer = {
            height: "56px",
            width: "100%",
        };
        const otpInput = {
            height: "56px",
            width: "57px",
            border: "1px solid #E0E7FF",
            flex: "1",
        };
        const canResend = otpResendSeconds === 0;

        return (
            <Stack alignItems="center" justifyContent="center">
                <Stack justifyContent="center">
                    <Image src={imageSrc} alt={brokerName || "navia"} height="auto" width={120} />
                </Stack>
                <Text variant="subtitle2" sx={{ my: { xs: 1, md: 2 } }}>
                    Login to {(brokerName || "navia").toUpperCase()}
                </Text>
                <TextField
                    label="Username"
                    value={authFormData.username}
                    onChange={(e) =>
                        this.handleAuthFormChange({
                            name: "username",
                            value: e.target.value,
                        })
                    }
                    helperText={authFormError.username}
                    error={authFormError.username}
                    sx={{ width: 1, mb: { xs: 4, md: 2 } }}
                />

                <TextField
                    type="password"
                    label="Password"
                    value={authFormData.password}
                    onChange={(e) =>
                        this.handleAuthFormChange({
                            name: "password",
                            value: e.target.value,
                        })
                    }
                    helperText={authFormError.password}
                    error={authFormError.password}
                    sx={{ width: 1, mb: { xs: 4, md: 2 } }}
                />

                {showOtpField ? (
                    <>
                        <OtpInput
                            className={otpContainer}
                            inputStyle={otpInput}
                            value={authFormData.otp}
                            numInputs={6}
                            isInputNum={true}
                            onChange={(e) =>
                                this.handleAuthFormChange({
                                    name: "otp",
                                    value: e,
                                })
                            }
                            separator={<span>&nbsp;</span>}
                        />
                        {authFormError.otp ? (
                            <Text sx={{ color: "error.main", width: 1, mt: 1 }}>{authFormError.otp}</Text>
                        ) : null}
                    </>
                ) : null}

                <Stack
                    variant="caption1"
                    sx={{
                        whiteSpace: "nowrap",
                        alignSelf: "flex-end",
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        flexFlow: "row wrap",
                        fontSize: "15px",
                        mt: 1,
                    }}
                >
                    <Button
                        component="span"
                        sx={{
                            fontSize: "15px",
                            color: canResend ? "#72aaff" : "#9e9e9e",
                            background: "transparent !important",
                            padding: "0",
                            boxShadow: "none !important",
                            display: "inline",
                            textTransform: "none",
                            cursor: canResend ? "pointer" : "default",
                            minWidth: "auto",
                            marginRight: "8px",
                        }}
                        onClick={() => canResend && this.handleAuthFormSubmit("SEND_OTP")}
                        loading={otpSentLoading}
                        disabled={!canResend && showOtpField}
                    >
                        {showOtpField ? "Resend OTP" : "Send OTP"}
                    </Button>
                    {showOtpField && otpResendSeconds > 0 ? (
                        <Text component="span" sx={{ fontSize: "14px", color: "#666" }}>
                            ({otpResendSeconds}s)
                        </Text>
                    ) : null}
                </Stack>
                {otpSuccessResponse ? (<Text sx={{ width: 1, mt: 1, color: "success.main" }}>{otpSuccessResponse}</Text>) : null}
                {otpFailureResponse ? (
                    <Text sx={{ width: 1, mt: 1, mb: 1, color: "error.main", textAlign: "center" }}>
                        {otpFailureResponse}
                    </Text>
                ) : null}
                <br />
                <JarvisButton
                    sx={{ mb: 3 }}
                    onClick={() => this.handleAuthFormSubmit("LOGIN")}
                    loading={isAuthLoading}
                    disabled={!showOtpField || String(authFormData.otp).length !== 6}
                >
                    Login
                </JarvisButton>
            </Stack>
        );
    }

    render() {
        const {
            isLoading,
            isOrderLoading,
            isOrderPlaced,
            isOrderSlow,
            statusCheckCount,
            authFormModalOpen,
            processDataModalOpen,
            processData: { brokerName },
        } = this.state;
        const { preOrderData } = this.props;

        // any stock not in a final status (complete / rejected / cancelled) counts as pending
        const hasPendingStocks =
            isOrderPlaced &&
            (preOrderData?.stockDetails || []).some(
                (sd) => !FINAL_ORDER_STATUSES.includes(`${sd.status || ""}`.toLowerCase())
            );
        const isFinishLocked = hasPendingStocks && statusCheckCount < MIN_STATUS_CHECKS_BEFORE_FINISH;

        return (
            <>
                <Loader loading={isLoading} />

                {!this.props.processData ? (
                    <Text>
                        {brokerName
                            ? `Selected broker: ${brokerName.toUpperCase()}`
                            : "No Broker Selected"}
                    </Text>
                ) : null}

                <Modal
                    disableClose={false}
                    disableExcapeKeyDown={true}
                    size="xs"
                    open={authFormModalOpen}
                    onClose={this.handleAuthFormModalClose}
                >
                    {this.renderLoginForm()}
                </Modal>

                <Modal
                    disableClose={isOrderPlaced || isOrderLoading}
                    disableExcapeKeyDown={true}
                    title="Order Data"
                    size="lg"
                    open={processDataModalOpen}
                    onClose={this.handleOrderdataModalClose}
                >
                    <Stack
                        direction="row"
                        alignItems="stretch"
                        justifyContent="flex-start"
                        sx={{ position: "relative", flexWrap: "wrap" }}
                    >

                        {(preOrderData?.stockDetails || []).map((sd, i) => (
                            <Stack
                                key={`stock_s_${i}`}
                                sx={{ flexGrow: 0, width: { xs: 1, sm: 0.5 } }}
                            >
                                <StockCard
                                    sx={{ border: `1px solid #e2e2e2` }}
                                    stockDetails={sd}
                                    cardType="SARAL"
                                    showRejectedRemark
                                />
                            </Stack>
                        ))}
                    </Stack>

                    {/* Navia can take a while to place the order */}
                    {isOrderLoading && !isOrderPlaced ? (
                        <Stack role="status" aria-live="polite" sx={{ mt: 3, mb: -2 }}>
                            {isOrderSlow ? (
                                <JarvisNote
                                    heading="Still processing with Navia."
                                    description="This can take up to a minute. Please keep this tab open and do not refresh until the order has been placed successfully."
                                />
                            ) : (
                                <JarvisNote
                                    heading="Please wait while we process your order."
                                    description="This may take a few moments. Do not refresh or close this tab until the order has been placed successfully."
                                />
                            )}
                        </Stack>
                    ) : null}

                    {/* Order placed but some stocks still pending: explain why Finish is disabled */}
                    {hasPendingStocks ? (
                        <Stack role="status" aria-live="polite" sx={{ mt: 3, mb: -2 }}>
                            <JarvisNote
                                heading="Some orders are still pending."
                                description={
                                    isFinishLocked
                                        ? "Tap Check Order Status to update."
                                        : "You can finish now. Any pending orders will continue to be processed by Navia."
                                }
                            />
                        </Stack>
                    ) : null}

                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        justifyContent="right"
                        width={1}
                        mt={3}
                        spacing={2}
                    >
                        {isOrderPlaced ? (
                            <>
                                <JarvisButton
                                    loading={isOrderLoading}
                                    disableEndIcon
                                    variant={"outlined"}
                                    onClick={() =>
                                        this.handleOrderProcessClick(
                                            "CHECK_ORDER_STATUS"
                                        )
                                    }
                                >
                                    Check Order Status
                                </JarvisButton>
                                <JarvisButton
                                    loading={isOrderLoading}
                                    disabled={isFinishLocked}
                                    disableEndIcon
                                    onClick={() =>
                                        this.handleOrderProcessClick(
                                            "FINISH_ORDER"
                                        )
                                    }
                                >
                                    Finish
                                </JarvisButton>
                            </>
                        ) : (
                            <JarvisButton
                                loading={isOrderLoading}
                                onClick={() =>
                                    this.handleOrderProcessClick("PLACE_ORDER")
                                }
                            >
                                Place Order Now
                            </JarvisButton>
                        )}
                    </Stack>
                </Modal>
            </>
        );
    }

}

export default withRouter(React.memo(BrokerNaviaContainer));
